import style from "./License.module.scss";
import LicenseRenewal from "./LicenseRenewal";
import LicenseDetail from "@/components/BaseComponents/Popups/LicenseDeail/Modal";
import RightSidebar from "@/components/BaseComponents/RightSidebar/RightSidebar";
import { useGetLicenseQuery } from "@/core/api/license/license";
import { useUserStore } from "@/core/store/UserDataStore";
import { dateToRu } from "@/services/date.service";
import { snackbarFail } from "@/services/snackbar.service";
import {
  Badge,
  BannerTimer,
  BannerWarningLine,
  Button,
  Skeleton,
  useSnackbars,
} from "@aurora/components";
import { GlyphAlert, GlyphInfo } from "@aurora/icons";
import cx from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useSearchParams } from "react-router-dom";

const License: FC = () => {
  const tarifPlan = "Энтерпрайз";

  const { snackbarInfo } = useSnackbars();
  const [searchParams] = useSearchParams();

  const [isShowLicenseDetailPopup, setShowLicenseDetailPopup] = useState(false);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);

  const { isManagerOrWatcher } = useUserStore();

  const {
    data: licenseData,
    error: isError,
    isLoading: loadData,
  } = useGetLicenseQuery(
    { refetch: isShowLicenseDetailPopup },
    { refetchOnMountOrArgChange: true },
  );

  const showTimer = useMemo(() => {
    return searchParams.has("timer") && !!licenseData;
  }, [licenseData]);

  useEffect(() => {
    if (isError && "status" in isError && isError.status === 403) {
      const fail: any = snackbarFail(
        "Недостаточно прав на выполнение операции",
      );
      snackbarInfo?.show(fail);
    }
  }, [isError]);

  const needUpdate = useMemo(() => {
    if (licenseData) {
      const validTo = Date.parse(licenseData.validTo);
      const now = Date.now();
      const _14day = 60 * 60 * 24 * 14 * 1000;

      return validTo - _14day < now;
    }
  }, [licenseData]);

  const licenseExpired = useMemo(() => {
    if (licenseData) {
      const validTo = Date.parse(licenseData.validTo);
      const now = Date.now();

      return validTo < now;
    }
  }, [licenseData]);

  //todo реализация закрытого доступа с странице только Админу
  const validTo = (
    <>
      {loadData ? (
        <Skeleton
          height={32}
          radius="r5"
          shape="line"
          variant="primary"
          width={100}
        />
      ) : (
        <>
          Действует до:
          <span>{licenseData ? dateToRu(licenseData.validTo) : "-"}</span>
        </>
      )}
    </>
  );

  return (
    <>
      <h1 className={cx("my-0 text-black")}>Лицензия</h1>

      {licenseData && (needUpdate || showTimer) && (
        <>
          {licenseExpired ? (
            <BannerTimer
              colorBackground={"rgba(255, 82, 82, 0.12)"}
              colorBackgroundTimer={"rgba(255, 82, 82, 0.12)"}
              colorText={"#EA3838"}
              colorTextTimer={"#EA3838"}
              deadline={new Date(Date.parse(licenseData.validTo)).toISOString()}
              description={
                "Для восстановления доступа к настройкам платформы необходимо продлить лицензию."
              }
              title={"Срок действия вашей лицензии истёк."}
            />
          ) : (
            <BannerTimer
              colorBackground={"rgba(251, 140, 0, 0.12)"}
              colorBackgroundTimer={"rgba(251, 140, 0, 0.12)"}
              colorText={"#FF8C21"}
              colorTextTimer={"#FF8C21"}
              deadline={new Date(Date.parse(licenseData.validTo)).toISOString()}
              description={
                "Рекомендуем начать процесс обновления уже сейчас, чтобы сохранить доступ к настройкам платформы."
              }
              title={"Срок действия вашей лицензии истекает."}
            />
          )}
        </>
      )}
      {!licenseData ? (
        <>
          <div className={cx(style.LicenseNulled, "py-8")}>
            <BannerWarningLine
              addonLeft={<GlyphAlert />}
              className={style.BannerWarning}
              title={
                "Для того, чтобы начать работу с Carrot, необходимо загрузить файл лицензии"
              }
              variant={"primary"}
            />
            <div className={cx(style.LicenseTariffLabel, "d-flex py-4")}>
              <GlyphInfo
                className={"cursor-pointer"}
                color={"#FF8C21"}
                onClick={() => {
                  setShowLicenseDetailPopup(true);
                }}
              />
              <Link target={"_blank"} to={"//fork-tech.ru/carrot"}>
                О Carrot
              </Link>
            </div>
            <Button
              disabled={isManagerOrWatcher}
              onClick={() => setIsRightSidebarOpen(true)}
              shape={"inline"}
              size={"m"}
              variant={"primary"}
            >
              Активировать лицензию
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className={"py-8"}>
            <div className={cx(style.LicenseTariffLabel, "d-flex py-4")}>
              Ваш тарифный план:
              <Badge color={"alert"} label={tarifPlan} />
              <GlyphInfo
                className={"cursor-pointer"}
                color={"#FF8C21"}
                onClick={() => {
                  setShowLicenseDetailPopup(true);
                }}
              />
            </div>
            <div className={cx(style.LicenseTariffLabel, "d-flex py-4")}>
              {validTo}
            </div>
          </div>
          <Button
            disabled={isManagerOrWatcher}
            onClick={() => setIsRightSidebarOpen(true)}
            shape={"inline"}
            size={"m"}
            variant={"primary"}
          >
            Обновить лицензию
          </Button>
        </>
      )}
      {isShowLicenseDetailPopup && (
        <LicenseDetail
          clickOnClose={() => {
            setShowLicenseDetailPopup(false);
          }}
          licenseData={licenseData}
        />
      )}

      {ReactDOM.createPortal(
        <>
          <RightSidebar
            onClose={() => {
              setIsRightSidebarOpen(false);
            }}
            show={isRightSidebarOpen}
            widthPercent={25}
          >
            {isRightSidebarOpen && (
              <LicenseRenewal callback={setIsRightSidebarOpen} />
            )}
          </RightSidebar>
        </>,
        document.body,
      )}
    </>
  );
};
export default License;
